/**
 * Mermaid node component for React Flow
 * Renders different shapes based on node data
 */

import { useCallback, useState } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { MermaidNodeData, MermaidNodeShape } from "../../utils"

import { Pencil, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useMermaidEditorStore } from "../../stores/mermaid-editor-store"

type MermaidNodeProps = {
  id: string
  data: MermaidNodeData
  selected?: boolean
}

function getShapeStyles(shape: MermaidNodeShape): string {
  const baseStyles =
    "min-w-[100px] min-h-[40px] flex items-center justify-center px-4 py-2 text-sm font-medium transition-[color,background-color,border-color,box-shadow] duration-150"

  switch (shape) {
    case "rounded":
      return cn(baseStyles, CATEGORY_TONES[0], "rounded-lg border-2")
    case "stadium":
      return cn(baseStyles, CATEGORY_TONES[1], "rounded-full border-2")
    case "subroutine":
      return cn(baseStyles, CATEGORY_TONES[2], "border-y-2 border-x-[6px] border-double")
    case "cylinder":
      return cn(
        baseStyles,
        "min-h-[50px] rounded-[50%/20%] bg-primary/10 text-foreground border-2 border-primary-ink"
      )
    case "circle":
      return cn(baseStyles, CATEGORY_TONES[3], "rounded-full aspect-square border-2")
    case "doubleCircle":
      return cn(
        baseStyles,
        CATEGORY_TONES[3],
        "rounded-full aspect-square border-2 ring-2 ring-foreground ring-offset-2 ring-offset-background"
      )
    case "asymmetric":
      return cn(
        baseStyles,
        CATEGORY_TONES[4],
        "[clip-path:polygon(10%_0%,100%_0%,100%_100%,10%_100%,0%_50%)] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "rhombus":
      return cn(
        baseStyles,
        CATEGORY_TONES[0],
        "[clip-path:polygon(50%_0%,100%_50%,50%_100%,0%_50%)] min-w-[120px] min-h-[60px] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "hexagon":
      return cn(
        baseStyles,
        CATEGORY_TONES[1],
        "[clip-path:polygon(25%_0%,75%_0%,100%_50%,75%_100%,25%_100%,0%_50%)] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "parallelogram":
      return cn(
        baseStyles,
        CATEGORY_TONES[2],
        "[clip-path:polygon(15%_0%,100%_0%,85%_100%,0%_100%)] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "parallelogramAlt":
      return cn(
        baseStyles,
        CATEGORY_TONES[2],
        "[clip-path:polygon(0%_0%,85%_0%,100%_100%,15%_100%)] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "trapezoid":
      return cn(
        baseStyles,
        CATEGORY_TONES[3],
        "[clip-path:polygon(10%_0%,90%_0%,100%_100%,0%_100%)] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "trapezoidAlt":
      return cn(
        baseStyles,
        CATEGORY_TONES[3],
        "[clip-path:polygon(0%_0%,100%_0%,90%_100%,10%_100%)] [filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))] dark:[filter:drop-shadow(1px_0_0_hsl(var(--foreground)))_drop-shadow(-1px_0_0_hsl(var(--foreground)))_drop-shadow(0_1px_0_hsl(var(--foreground)))_drop-shadow(0_-1px_0_hsl(var(--foreground)))_drop-shadow(2px_0_0_hsl(var(--foreground)))_drop-shadow(-2px_0_0_hsl(var(--foreground)))_drop-shadow(0_2px_0_hsl(var(--foreground)))_drop-shadow(0_-2px_0_hsl(var(--foreground)))]"
      )
    case "rectangle":
    default:
      return cn(baseStyles, "bg-card text-foreground border-2 border-border")
  }
}

export function MermaidNode({ id, data, selected }: MermaidNodeProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(data.label)
  const { deleteElements } = useReactFlow()
  const updateNodeData = useMermaidEditorStore((state) => state.updateNodeData)

  const handleDoubleClick = useCallback(() => {
    setEditValue(data.label)
    setIsEditing(true)
  }, [data.label])

  const handleSave = useCallback(() => {
    if (editValue.trim()) {
      updateNodeData(id, { label: editValue.trim() })
    }
    setIsEditing(false)
  }, [id, editValue, updateNodeData])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleSave()
      } else if (event.key === "Escape") {
        setIsEditing(false)
        setEditValue(data.label)
      }
    },
    [handleSave, data.label]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  return (
    <div className="group relative">
      <Handle
        className="!border-foreground/50 !bg-background !size-3 !border-2"
        position={Position.Top}
        type="target"
      />

      <div
        className={cn(
          getShapeStyles(data.shape),
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          "cursor-pointer"
        )}
        onDoubleClick={handleDoubleClick}
      >
        {isEditing ? (
          <Input
            /* eslint-disable-next-line jsx-a11y/no-autofocus */
            autoFocus
            className="nodrag h-6 min-w-[60px] border-none bg-transparent p-0 text-center text-sm focus-visible:ring-0"
            value={editValue}
            onBlur={handleSave}
            onChange={(event) => setEditValue(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <span className="select-none">{data.label}</span>
        )}
      </div>

      <Handle
        className="!border-foreground/50 !bg-background !size-3 !border-2"
        position={Position.Bottom}
        type="source"
      />

      <Handle
        className="!border-foreground/50 !bg-background !size-3 !border-2"
        id="left"
        position={Position.Left}
        type="target"
      />

      <Handle
        className="!border-foreground/50 !bg-background !size-3 !border-2"
        id="right"
        position={Position.Right}
        type="source"
      />

      {selected && (
        <div className="absolute -top-8 -right-2 flex gap-1">
          <Button
            className="nodrag size-6 rounded-full p-0 shadow-md"
            size="icon"
            variant="secondary"
            onClick={() => {
              setEditValue(data.label)
              setIsEditing(true)
            }}
          >
            <Pencil className="size-3" />
          </Button>
          <Button
            className="nodrag size-6 rounded-full p-0 shadow-md"
            size="icon"
            variant="destructive"
            onClick={handleDelete}
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      )}
    </div>
  )
}
